import React, { useState, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { withStyles, ClassNameMap } from '@mui/styles'
import { RootState } from '../../../../redux/store'
import stylesheet from '../detail-container.stylesheet'
import {
	Accordion, AccordionDetails, AccordionSummary, Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import Draggable from "react-draggable"
import EmptyCardComponent from './empty-card.component';
import CategoryQuesionnariesComponent from '../../../questionnaires/category-quesionnaries.component'
import { formatDisplayName } from '../../../../utils/display-name-formatter'

interface IProps {
	open: boolean
	input: any
	handleClose: any
	classes: ClassNameMap
}

const DependentInfoComponent: React.FC<IProps> = ({ open, input, handleClose, classes }) => {
	const contextRef = useRef(null) as any
	const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)

	const [expandedPanels, setExpandedPanels] = useState < { [key: string]: boolean } > ({
		questionnaires: true,
		conditions: true,
		thresholds: true,
		measurements: true
	})
	const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent) => {
		setExpandedPanels(prev => ({
			...prev,
			[panel]: !prev[panel]
		}))
	}

	// Parse rationale JSON if present, fall back to synopsis
	const parsedSynopsis = useMemo(() => {
		// Prefer rationale over synopsis
		const source = input?.rationale || input?.synopsis
		if (!source) return null
		try {
			return typeof source === 'string' ? JSON.parse(source) : source
		} catch {
			// If not valid JSON, treat as plain text description
			return { description: source }
		}
	}, [input?.rationale, input?.synopsis])

	const renderSynopsis = () => {
		// Only show if we have description or logic
		if (!parsedSynopsis?.description && !parsedSynopsis?.logic) return null

		return (
			<Box className={classes.contextualHelpBox} sx={{ width: '100%' }}>
				<Grid container spacing={1} sx={{ padding: '0.5rem 1rem 0.15rem 0.8rem !important' }}  alignItems="flex-start">
					{parsedSynopsis?.description && (
						<>
							<Grid item xs={1.5}>
								<Typography variant="subtitle2" className={classes.desc}>Description</Typography>
							</Grid>
							<Grid item xs={10.5}>
								<Typography variant="body2" className={classes.desc}>{parsedSynopsis.description}</Typography>
							</Grid>
						</>
					)}
					{parsedSynopsis?.logic && (
						<>
							<Grid item xs={1.5}>
								<Typography variant="subtitle2" className={classes.desc}>Logic</Typography>
							</Grid>
							<Grid item xs={10.5}>
								<Typography variant="body2" className={classes.desc} sx={{ whiteSpace: 'pre-wrap' }}>{parsedSynopsis.logic}</Typography>
							</Grid>
						</>
					)}
				</Grid>
			</Box>
		)
	}

	const renderMeasurements = () => {
		if (!input?.dependent_measurements?.length) return null

		return (
			<Accordion
				key={`measurements`}
				square
				disableGutters
				expanded={expandedPanels.measurements}
				className={classes.fullWidth}
				sx={{ backgroundColor: 'var(--color-background-subtle) !important', '& .MuiSvgIcon-root': { fontSize: '0.75rem!important' } }}
			>
				<AccordionSummary
					className={classes.zeroPadding}
					expandIcon={<ExpandMoreIcon />}
					sx={{
						minHeight: 'auto !important',
						'&.Mui-expanded': { minHeight: 'auto !important' },
						'& .MuiAccordionSummary-content': {
							margin: '4px 0 !important',
							pointerEvents: 'none'
						},
						'& .MuiAccordionSummary-content.Mui-expanded': {
							margin: '4px 0 !important'
						},
						'& .MuiAccordionSummary-expandIconWrapper': {
							pointerEvents: 'auto'
						}
					}}
					onClick={handleAccordionChange('measurements')}
				>
					<Typography
						variant='subtitle2'
						className={classes.subHeader}
						sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
					>
						{`Dependent measurements`}
					</Typography>
				</AccordionSummary>
				<AccordionDetails sx={{ pt: 0, pb: 0.5, px: 1 }}>
					<Grid container spacing={0.5}>
						{input.dependent_measurements.map((measurement: string, index: number) => (
							<Grid item xs={12} key={`measurement-${index}`}>
								<Typography variant="body2" className={classes.desc}>
									{measurement.replace(/_/g, ' ')}
								</Typography>
							</Grid>
						))}
					</Grid>
				</AccordionDetails>
			</Accordion>
		)
	}

	const renderQuestionnaires = () => {
		return (input?.dependent_questionnaires)?.length > 0
			? <Accordion
				key={`dependent_questionnaires`}
				square
				disableGutters
				expanded={expandedPanels.questionnaires}
				className={classes.fullWidth}
				sx={{ backgroundColor: 'var(--color-background-subtle) !important', '& .MuiSvgIcon-root': { fontSize: '0.75rem!important' } }}
			>
				<AccordionSummary
					className={classes.zeroPadding}
					expandIcon={<ExpandMoreIcon />}
					sx={{
						// width: '20%',
						marginBottom: '0rem !important',
						minHeight: 'auto !important',
						'&.Mui-expanded': { minHeight: 'auto !important' },
						'& .MuiAccordionSummary-content': {
							margin: '4px 0 !important',
							pointerEvents: 'none'
						},
						'& .MuiAccordionSummary-content.Mui-expanded': {
							margin: '4px 0 !important'
						},
						'& .MuiAccordionSummary-expandIconWrapper': {
							pointerEvents: 'auto'
						}
					}}
					onClick={handleAccordionChange('questionnaires')}
				>
					<Typography
						variant='subtitle2'
						className={classes.subHeader}
						sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
					>
						{`Clinical assessments`}
					</Typography>
				</AccordionSummary>
				<AccordionDetails sx={{ margin: '-0.8rem 0rem 0rem 0.2rem !important' }}>
					<CategoryQuesionnariesComponent
						open={true}
						isSearch={true}
						title={`Clinical assessments`}
						item={input?.dependent_questionnaires || []}
						input={{
							dependent_questionnaires: true
						}}
						handleClose={handleClose}
					/>
				</AccordionDetails>
			</Accordion>
			: null
	}

	const renderConditionByThresholds = (item: any) => {
		return (
			<Stack direction={'column'} spacing={0} pl={0.4}>
				{item?.thresholds?.length > 0
					? <Stack direction={'column'} spacing={0.5}>
						{[...item?.thresholds || []]
							?.sort((a: any, b: any) => {
								const nameA = (a?.condition_display_name || a?.condition_name || '').toLowerCase()
								const nameB = (b?.condition_display_name || b?.condition_name || '').toLowerCase()
								return nameA.localeCompare(nameB)
							})
							?.map((threshold: any, thresholdIndex: number) => {
								const displayTitle = item?.condition_display_name || item?.condition_name
								const thresholdInfo = `${threshold.threshold_name} (PLR: ${threshold.lr_pos_index}, NLR: ${threshold.lr_neg_index})`

								return (
									<Stack direction={'row'} spacing={0.3} justifyContent={'left'} alignItems={'center'} key={thresholdIndex}>
										<Typography className={classes.desc} variant='body2'>
											{displayTitle}{' - '}{thresholdInfo}
										</Typography>
									</Stack>
								)
							})}
					</Stack>
					: <Typography variant="body2" className={classes.desc}>
						{item?.condition_display_name || item?.condition_name}
					</Typography>
				}
			</Stack>
		)
	}

	const getPrgList = () => {
		return input?.programs?.map((p: any) => {
			const prgObj = patientDetail.program_enrollments?.find((f: { program: any }) => f.program === p) || {}
			return prgObj.program_display_name
		})
			?.filter((name: any) => name !== undefined && name !== null && name !== '')
			?.join(', ') || ''
	}

	const renderConditions = () => {
		return (input?.conditions)?.length > 0
			? <Accordion
				key={`conditions`}
				square
				disableGutters
				expanded={expandedPanels.conditions}
				className={classes.fullWidth}
				sx={{ backgroundColor: 'var(--color-background-subtle) !important', '& .MuiSvgIcon-root': { fontSize: '0.75rem!important' } }}
			>
				<AccordionSummary
					className={classes.zeroPadding}
					expandIcon={<ExpandMoreIcon />}
					sx={{
						minHeight: 'auto !important',
						'&.Mui-expanded': { minHeight: 'auto !important' },
						'& .MuiAccordionSummary-content': {
							margin: '4px 0 !important',
							pointerEvents: 'none'
						},
						'& .MuiAccordionSummary-content.Mui-expanded': {
							margin: '4px 0 !important'
						},
						'& .MuiAccordionSummary-expandIconWrapper': {
							pointerEvents: 'auto'
						}
					}}
					onClick={handleAccordionChange('conditions')}
				>
					<Typography
						variant='subtitle2'
						className={classes.subHeader}
						sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
					>
						{input?.isDiagnosticTest
							? `The conditions relevant to ${input.programs ? getPrgList() : ''} with evidence most impacted by this test are: `
							: `Conditions`
						}
					</Typography>
				</AccordionSummary>
				<AccordionDetails sx={{ pt: 0, pb: 0.5, px: 1 }}>
					<Grid container spacing={0.5}>
						{(input?.conditions)?.map((item: any, index: number) => (
							<Grid item xs={12} key={`condition - ${index} `}>
								{item?.thresholds
									? renderConditionByThresholds(item)
									: typeof item === 'object'
										? <Stack direction={'row'} spacing={0.5} pl={0.4} justifyContent={'left'} alignItems={'center'}>
											<Typography variant="body2" className={classes.desc}>
												{item?.condition_display_name || item?.condition_name}
											</Typography>
											<Typography component={'span'} variant="body2" className={classes.desc}>
												{`(sensitivity: ${item?.sensitivity || '- '}, specificity: ${item?.specificity || '-'
													})`}
											</Typography>
										</Stack>
										: typeof item === 'string'
											? <Typography variant="body2" className={classes.desc}>
												{item}
											</Typography>
											: null
								}
							</Grid>
						))}
					</Grid>
				</AccordionDetails>
			</Accordion>
			: null
	}

	const renderThresholds = () => {
		return (input?.threshold)?.length > 0
			? <Accordion
				key={`thresholds`}
				square
				disableGutters
				expanded={expandedPanels.thresholds}
				className={classes.fullWidth}
				sx={{ backgroundColor: 'var(--color-background-subtle) !important', '& .MuiSvgIcon-root': { fontSize: '0.75rem!important' } }}
			>
				<AccordionSummary
					className={classes.zeroPadding}
					expandIcon={<ExpandMoreIcon />}
					sx={{
						minHeight: 'auto !important',
						'&.Mui-expanded': { minHeight: 'auto !important' },
						'& .MuiAccordionSummary-content': {
							margin: '4px 0 !important',
							pointerEvents: 'none'
						},
						'& .MuiAccordionSummary-content.Mui-expanded': {
							margin: '4px 0 !important'
						},
						'& .MuiAccordionSummary-expandIconWrapper': {
							pointerEvents: 'auto'
						}
					}}
					onClick={handleAccordionChange('thresholds')}
				>
					<Typography
						variant='subtitle2'
						className={classes.subHeader}
						sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
					>
						{`Diagnostic thresholds`}
					</Typography>
				</AccordionSummary>
				<AccordionDetails sx={{ pt: 0, pb: 0.5, px: 1 }}>
					<Grid container spacing={0.6}>
						{(input?.threshold)
							?.sort((a: any, b: any) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()))
							?.map((item: any, index: number) => (
								<Grid item xs={12} key={`condition - ${index} `}>
									<Typography variant="body2" className={classes.desc}>
										{formatDisplayName(item || '')}
									</Typography>
								</Grid>
							))}
					</Grid>
				</AccordionDetails>
			</Accordion>
			: null
	}

	const renderTests = () => {
		const conditionList = [...(input?.tests_per_cond) || []] as Array<{ name: string; display_name: string }>

		return <Accordion
			key={`tests`}
			square
			disableGutters
			expanded={expandedPanels.thresholds}
			className={classes.fullWidth}
			sx={{ backgroundColor: 'var(--color-background-subtle) !important', '& .MuiSvgIcon-root': { fontSize: '0.75rem!important' } }}
		>
			<AccordionSummary
				className={classes.zeroPadding}
				expandIcon={<ExpandMoreIcon />}
				sx={{
					minHeight: 'auto !important',
					'&.Mui-expanded': { minHeight: 'auto !important' },
					'& .MuiAccordionSummary-content': {
						margin: '4px 0 !important',
						pointerEvents: 'none'
					},
					'& .MuiAccordionSummary-content.Mui-expanded': {
						margin: '4px 0 !important'
					},
					'& .MuiAccordionSummary-expandIconWrapper': {
						pointerEvents: 'auto'
					}
				}}
				onClick={handleAccordionChange('thresholds')}
			>
				<Typography
					variant='subtitle2'
					className={classes.subHeader}
					sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
				>
					{`Tests`}
				</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{ pt: 0, pb: 0.5, px: 1 }}>
				<Grid container spacing={0.5}>
					{conditionList
						.sort((a, b) => a.display_name.toLocaleLowerCase().localeCompare(b.display_name.toLocaleLowerCase()))
						.map((item, index) => (
							<Grid item xs={12} key={`condition-${index}`}>
								<Typography variant="body2" className={classes.desc}>
									{formatDisplayName(item?.display_name || item?.name || '')}
								</Typography>
							</Grid>
						))}
				</Grid>
			</AccordionDetails>
		</Accordion>
	}

	return (
		<>
			{open && (
				<Draggable ref={contextRef} defaultPosition={{ x: 80, y: 20 }} handle='.dependent-info-dialog'>
					<Dialog
						id={`info - ${input?.title} `}
						fullWidth={true}
						maxWidth="sm"
						open={open}
						onClose={handleClose}
						hideBackdrop
						disableScrollLock
						PaperProps={{ sx: { maxHeight: 'none' } }}
						sx={{ position: "absolute", left: '10rem', top: '8rem' }}
					>
						<DialogTitle className={classes.contextualHelpHeader} classes={{ root: 'dependent-info-dialog' }}>
							<Grid container alignItems={'center'} justifyContent="space-between">
								<Grid item>
									<Typography id="modal-modal-title" variant="body1" className={classes.subtitle2}>
										<strong>{input?.title}</strong>
									</Typography>
								</Grid>
								<Grid item>
									<IconButton size="small" onClick={() => handleClose()}>
										<CloseOutlinedIcon className={classes.editIcon} />
									</IconButton>
								</Grid>
							</Grid>
						</DialogTitle>
						<DialogContent
							className={`${classes.contextualHelpContext} ${classes.zeroPadding} `}
							sx={{ backgroundColor: 'var(--color-background-subtle) !important', overflow: 'visible' }}
						>
							<Box
								className={classes.contextualHelpBox}
								sx={{
									minHeight: '15rem !important',
									maxHeight: '25rem',
									overflowY: 'auto !important',
									padding: '0.8rem 0rem 0.5rem 1rem !important'
								}}
							>
								{(input?.dependent_questionnaires?.length > 0 || input?.conditions?.length > 0 || input?.threshold?.length > 0 || parsedSynopsis || input?.dependent_measurements?.length > 0)
									? <Stack direction='column' spacing={0.5} alignItems={'flex-start'} justifyContent={'flex-start'}>
										{renderSynopsis()}
										{renderQuestionnaires()}
										{renderMeasurements()}
										{renderConditions()}
										{(input?.tests_per_cond)?.length > 0 && renderTests()}
										{renderThresholds()}
									</Stack>
									: <Box p={2}><EmptyCardComponent /></Box>
								}
							</Box>
						</DialogContent>
					</Dialog>
				</Draggable>
			)}
		</>
	)
}

export default withStyles(stylesheet)(DependentInfoComponent)